import type {
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useEffect, useState } from 'react';
import {
  ClientError,
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import type {
  IReceiptBatchPhoenixApi,
  ReceiptBatch,
  ReceiptBatchJob,
  ReceiptBatchJobResult,
  ReceiptBatchListParams,
  ReceiptBatchListResult,
  ReceiptBatchResult,
  UpdateReceiptBatch,
  UpdateReceiptBatchEntry,
} from './api';
import { ReceiptBatchPhoenixApi } from './api';
import {
  CREATE_RECEIPT_BATCH,
  CREATE_RECEIPT_BATCH_ENTRY,
  DELETE_RECEIPT_BATCH,
  DELETE_RECEIPT_BATCH_ENTRY_KEY,
  RECEIPT_BATCH_KEY,
  RECEIPT_BATCH_LIST_KEY,
  RECEIPT_BATCH_LIST_PAGINATED_KEY,
  RECEIPTS_AND_RECEIPT_BATCHES_KEY,
  RECEIPTS_KEY_NURSING_HOME,
  UPDATE_RECEIPT_BATCH,
  UPDATE_RECEIPT_BATCH_ENTRY,
} from '../transaction/queryKeys';
import type {
  ReceiptBatchGetDto,
  ReceiptBatchJobState,
  ReceiptBatchOwner,
} from '@pflegenavi/shared/api';
import { RECEIPT_BATCH_PAYMENT_STATUS } from '../payment';
import { useTranslation } from 'react-i18next';
import {
  CASH_LIST_TRANSACTIONS_KEY,
  CASH_TRANSACTION_GROUP_KEY,
  CASH_TRANSACTION_GROUP_LIST_KEY,
} from '../cash-management/queryKeys';
import {
  CASH_LIST_CONFIGURATION_PHOENIX_KEY,
  CASH_LIST_TRANSACTIONS_PHOENIX_KEY,
  CASH_LISTS_PHOENIX_KEY,
  CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY,
  CASH_TRANSACTION_GROUP_PHOENIX_KEY,
} from '../cash-management-phoenix/queryKeys';
import {
  ALL_SHOW_RESIDENT_KEY,
  RESIDENTS_PHOENIX_KEY,
} from '../resident-phoenix/queryKeys';
import { RESIDENTS_KEY } from '../resident/queryKeys';
import { getFinanceKey } from '../reporting-phoenix';
import {
  RECEIPT_BATCH_LIST_PHOENIX_KEY,
  RECEIPT_BATCH_PHOENIX_KEY,
} from './queryKeys';
import { PLIANT_TRANSACTIONS_KEY } from '../pliant-phoenix/queryKeys';
import { TRANSACTIONS_PHOENIX_KEY } from '../transaction-phoenix/queryKeys';

const ApiContext = createContext<IReceiptBatchPhoenixApi | undefined>(
  undefined
);
const {
  useApi: useReceiptBatchPhoenixApi,
  ApiProvider: ReceiptBatchPhoenixApiProvider,
} = makeApiProvider({
  name: 'ReceiptBatchPhoenix',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) => {
    return new ReceiptBatchPhoenixApi(tenantId, auth, apiUrl);
  },
});

export {
  useReceiptBatchPhoenixApi,
  ReceiptBatchPhoenixApiProvider,
  ApiContext as ReceiptBatchPhoenixApiContext,
  UpdateReceiptBatch,
  ReceiptBatch,
  ReceiptBatchListParams,
  ReceiptBatchListResult,
};

export const transformPhoenixReceiptBatchToLegacyReceiptBatch = (
  row: ReceiptBatch
): ReceiptBatchGetDto => {
  return {
    id: row.id,
    createdOn: row.created_on,
    nursingHomeId: row.nursing_home_id,
    receiptDate: row.receipt_date!,
    state: row.job?.status as ReceiptBatchJobState | undefined,
    cashListTransactionGroupData: row.cash_transaction_group_list
      ? {
          id: row.cash_transaction_group_list.id,
          cashListId: row.cash_transaction_group_list.cash_list_id,
          updateDate: row.cash_transaction_group_list.update_date,
          semanticCreationDate:
            row.cash_transaction_group_list.semantic_creation_date,
          cashTransactionNotes:
            row.cash_transaction_group_list.cash_transaction_notes,
          cashAmount: row.cash_transaction_group_list.cash_amount,
          separateFeeAmount:
            row.cash_transaction_group_list.separate_fee_amount,
          immediateFeeAmount:
            row.cash_transaction_group_list.immediate_fee_amount,
          linkCount: row.cash_transaction_group_list.link_count,
          linkedAmount: row.cash_transaction_group_list.linked_amount,
          linkedSeparateFeeAmount:
            row.cash_transaction_group_list.linked_separate_fee_amount,
          linkedImmediateFeeAmount:
            row.cash_transaction_group_list.linked_immediate_fee_amount,
        }
      : undefined,
    cashListTransactionGroupId: row.cash_list_transaction_group_id ?? undefined,
    receiptImageIds: row.receipt_image_ids,
    receiptType: row.receipt_type.id,
    serviceProviderId: row.service_provider?.id,
    serviceProviderName: row.service_provider?.name,
    serviceProvider: row.service_provider?.name,
    lastUpdatedOn: row.updated_on,
    title: 'Deprecated',
    owner: row.owner as ReceiptBatchOwner,
    submissionDate: row.job?.submission_date,
    receiptTypeName: row.receipt_type.name,
    serviceProviderRequired: row.receipt_type.requires_service_provider,
    submissionLocked: row.submission_locked,
    useIndividualReceiptDates: row.use_individual_dates,
    generateReceipts: row.generate_receipts,
    earnings: row.earnings,
    receiptBatchEntries: (row.receipt_batch_entries || []).map((entry) => ({
      id: entry.id,
      resident: {
        id: entry.resident.id,
        firstName: entry.resident.firstname,
        lastName: entry.resident.lastname,
        gender: entry.resident.gender,
        state: entry.resident.state,
      },
      amount: entry.amount,
      additionalFeeAmount: entry.additional_fee_amount,
      includedFeeAmount: entry.included_fee_amount,
      receiptImageIds: entry.receipt_image_ids,
      notes: entry.notes,
      receiptDate: entry.receipt_date,
      lineItems: entry.line_items?.map((lineItem) => ({
        id: lineItem.id,
        amountPerItem: lineItem.amount_per_item,
        count: lineItem.count,
        name: lineItem.name,
        taxes: lineItem.taxes?.map((tax) => ({
          id: tax.id,
          taxAmount: parseFloat(tax.tax_amount),
          countryId: tax.country_id ?? '',
          taxName: tax.tax_name,
          explanationShort: tax.explanation_short,
          explanationLong: tax.explanation_long,
          displayExplanation: tax.display_explanation,
          paragraph: tax.paragraph,
          taxAbbreviation: tax.tax_abbreviation,
        })),
      })),
    })),
    recurringItem: row.recurring_item_receipt_batch
      ? {
          id: row.recurring_item_receipt_batch.id,
          name: row.recurring_item_receipt_batch.name,
          amount: row.recurring_item_receipt_batch.amount,
          archived: row.recurring_item_receipt_batch.archived,
          autoAddResidents: row.recurring_item_receipt_batch.auto_add_residents,
          chargeFullPeriod: row.recurring_item_receipt_batch.charge_full_period,
          computeDaily: row.recurring_item_receipt_batch.compute_daily,
          taxCode: row.recurring_item_receipt_batch.tax_code
            ? {
                id: row.recurring_item_receipt_batch.tax_code.id,
                taxName: row.recurring_item_receipt_batch.tax_code.tax_name,
                taxAbbreviation:
                  row.recurring_item_receipt_batch.tax_code.tax_abbreviation,
                taxAmount: row.recurring_item_receipt_batch.tax_code.tax_amount,
                explanationShort:
                  row.recurring_item_receipt_batch.tax_code.explanation_short,
                explanationLong:
                  row.recurring_item_receipt_batch.tax_code.explanation_long,
                displayExplanation:
                  row.recurring_item_receipt_batch.tax_code.display_explanation,
                paragraph: row.recurring_item_receipt_batch.tax_code.paragraph,
              }
            : undefined,
          generateReceipts: row.recurring_item_receipt_batch.generate_receipts,
        }
      : undefined,
  };
};

export const toReceiptBatch = ({ data }: ReceiptBatchResult): ReceiptBatch => {
  return {
    ...data,
    receipt_date: data.receipt_date ? new Date(data.receipt_date) : undefined,
    created_on: new Date(data.created_on),
    updated_on: new Date(data.updated_on),
    job: data.job
      ? {
          ...data.job,
          submission_date: new Date(data.job.submission_date),
        }
      : data.job,
    receipt_batch_entries: data.receipt_batch_entries.map((entry) => ({
      ...entry,
      receipt_date: entry.receipt_date
        ? new Date(entry.receipt_date)
        : undefined,
    })),
  };
};

export const useGetReceiptBatchListPhoenix = (
  params: ReceiptBatchListParams,
  options?: Omit<
    UseQueryOptions<ReceiptBatchListResult, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<ReceiptBatchListResult, unknown> => {
  const select = useCallback((data: ReceiptBatchListResult) => {
    return {
      ...data,
      data: data.data.map((batch) => ({
        ...batch,
        receipt_date: batch.receipt_date
          ? new Date(batch.receipt_date)
          : undefined,
        min_receipt_date: batch.min_receipt_date
          ? new Date(batch.min_receipt_date)
          : undefined,
        max_receipt_date: batch.max_receipt_date
          ? new Date(batch.max_receipt_date)
          : undefined,
        job: batch.job
          ? {
              ...batch.job,
              submission_date: new Date(batch.job.submission_date),
            }
          : null,
      })),
    };
  }, []);

  return useApiQuery(
    useReceiptBatchPhoenixApi,
    RECEIPT_BATCH_LIST_PHOENIX_KEY(params),
    (api) => api.getReceiptBatches(params),
    {
      select,
      ...options,
    }
  );
};

export const useGetReceiptBatchPhoenix = (
  receiptBatchId: string | undefined,
  options?: Omit<
    UseQueryOptions<ReceiptBatchResult, unknown, ReceiptBatchGetDto>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<ReceiptBatchGetDto, unknown> => {
  const select = useCallback((data: ReceiptBatchResult) => {
    const part1 = toReceiptBatch(data);
    return transformPhoenixReceiptBatchToLegacyReceiptBatch(part1);
  }, []);

  return useApiQuery(
    useReceiptBatchPhoenixApi,
    RECEIPT_BATCH_PHOENIX_KEY(receiptBatchId),
    (api) => {
      if (receiptBatchId === undefined) {
        throw new Error('receiptBatchId is undefined');
      }

      return api.getReceiptBatch({
        params: {
          batchId: receiptBatchId,
        },
      });
    },
    {
      select,
      // Ensure the query is always disabled when receiptBatchId is undefined,
      // even if the caller provides their own enabled option
      enabled: receiptBatchId !== undefined && (options?.enabled ?? true),
      ...options,
    }
  );
};

export const useGetReceiptBatchPhoenixCallback = (
  receiptBatchId: string
): (() => Promise<ReceiptBatchGetDto>) => {
  const api = useReceiptBatchPhoenixApi();
  const select = useCallback((data: ReceiptBatchResult) => {
    const part1 = toReceiptBatch(data);
    return transformPhoenixReceiptBatchToLegacyReceiptBatch(part1);
  }, []);
  return useCallback(async () => {
    const result = await api.getReceiptBatch({
      params: { batchId: receiptBatchId },
    });
    return select(result);
  }, [api, receiptBatchId, select]);
};

export const useCreateReceiptBatchPhoenix = (): UseMutationResult<
  ReceiptBatch,
  unknown,
  Omit<UpdateReceiptBatch, 'id'>
> => {
  const api = useReceiptBatchPhoenixApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    ReceiptBatch,
    unknown,
    Omit<UpdateReceiptBatch, 'id'>
  >({
    mutationKey: CREATE_RECEIPT_BATCH,
    mutationFn: (data) =>
      api.postReceiptBatch({ body: data }).then((res) => toReceiptBatch(res)),
    onSuccess: async (data) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_KEY(data.nursing_home_id),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PAGINATED_KEY({
            nursingHomeId: data.nursing_home_id,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_AND_RECEIPT_BATCHES_KEY(data.nursing_home_id),
        }),
        queryClient.invalidateQueries({
          queryKey: PLIANT_TRANSACTIONS_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
      ]);
    },
  });
  return result;
};

const useHandleError = (
  errorSnackbar: (error: Error, message: string) => void
) => {
  const { t } = useTranslation();
  return useCallback(
    (e: unknown) => {
      if (e instanceof ClientError && 'errors' in e) {
        // @ts-expect-error costCenter untyped
        if (e.errors?.costCenter?.includes("can't be blank")) {
          errorSnackbar(
            e,
            t(`receipt-batch.preview.error-missing-cost-center`)
          );
        }
        // @ts-expect-error serviceProvider untyped
        else if (e.errors?.serviceProvider?.includes("can't be blank")) {
          errorSnackbar(
            e,
            t(`receipt-batch.preview.error-missing-service-provider`)
          );
        }
        return;
      }

      errorSnackbar(e as Error, t('errors.something-went-wrong'));
    },
    [errorSnackbar, t]
  );
};

export const useGenerateReceiptBatchPreview = (
  errorSnackbar: (error: Error, message: string) => void
): UseMutationResult<
  Blob,
  unknown,
  {
    batchId: string;
  }
> => {
  const api = useReceiptBatchPhoenixApi();

  const handleError = useHandleError(errorSnackbar);

  const result = useMutation<
    Blob,
    unknown,
    {
      batchId: string;
    }
  >({
    mutationKey: ['GENERATE_RECEIPT_BATCH_REVIEW'],
    mutationFn: (data) =>
      api.postReceiptBatchPreview({
        params: { batchId: data.batchId },
      }),
    onError: handleError,
  });
  return result;
};

export const useGenerateReceiptBatchEntryPreview = (
  errorSnackbar: (error: Error, message: string) => void
): UseMutationResult<
  Blob,
  unknown,
  {
    batchId: string;
    entryId: string;
  }
> => {
  const api = useReceiptBatchPhoenixApi();
  const handleError = useHandleError(errorSnackbar);
  const result = useMutation<
    Blob,
    unknown,
    {
      batchId: string;
      entryId: string;
    }
  >({
    mutationKey: ['GENERATE_RECEIPT_BATCH_ENTRY_REVIEW'],
    mutationFn: (data) => {
      return api.postReceiptBatchEntryPreview({
        params: {
          batchId: data.batchId,
          entryId: data.entryId,
        },
      });
    },
    onError: handleError,
  });
  return result;
};

export const usePatchReceiptBatchPhoenix = (
  options?: UseMutationOptions<ReceiptBatch, unknown, UpdateReceiptBatch>
): UseMutationResult<ReceiptBatch, unknown, UpdateReceiptBatch> => {
  const queryClient = useQueryClient();
  const api = useReceiptBatchPhoenixApi();
  const result = useMutation<ReceiptBatch, unknown, UpdateReceiptBatch>({
    mutationKey: UPDATE_RECEIPT_BATCH,
    mutationFn: (data) =>
      api
        .patchReceiptBatch({
          params: { batchId: data.id },
          body: data,
        })
        .then(toReceiptBatch),
    onSuccess: (data, variables, optionsIn, context) => {
      const promise = options?.onSuccess?.(data, variables, optionsIn, context);
      return Promise.all([
        ...(promise ? [promise] : []),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_KEY(variables.nursing_home_id),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PAGINATED_KEY({
            nursingHomeId: variables.nursing_home_id,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_AND_RECEIPT_BATCHES_KEY(variables.nursing_home_id),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_KEY(data.id),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_PHOENIX_KEY(data.id),
        }),

        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_PAYMENT_STATUS(data.id),
        }),
        queryClient.invalidateQueries({
          queryKey: PLIANT_TRANSACTIONS_KEY(),
        }),
        // Invalidate cash transaction groups when receipt batch is updated
        // The cash transaction group's linked amounts depend on receipt batch amounts
        ...(data.cash_list_transaction_group_id
          ? [
              queryClient.invalidateQueries({
                queryKey: CASH_TRANSACTION_GROUP_PHOENIX_KEY(
                  data.cash_list_transaction_group_id
                ),
              }),
              queryClient.invalidateQueries({
                queryKey: CASH_TRANSACTION_GROUP_KEY(
                  data.cash_list_transaction_group_id
                ),
              }),
            ]
          : []),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
      ]);
    },
    ...options,
  });
  return result;
};

export const useCreateReceiptBatchCashTransactionGroup = ({
  nursingHomeId,
}: {
  nursingHomeId?: string;
}): UseMutationResult<
  ReceiptBatch,
  unknown,
  {
    batchId: string;
    cashListId: string;
    coins?: Array<{ factor: number; amount: number }>;
    bankAccountAmount?: number;
    notes?: string;
  }
> => {
  const queryClient = useQueryClient();
  const api = useReceiptBatchPhoenixApi();
  return useMutation({
    mutationFn: ({ batchId, cashListId, coins, bankAccountAmount, notes }) =>
      api
        .postReceiptBatchCashTransactionGroup({
          params: { batchId },
          body: {
            cash_list_id: cashListId,
            coins,
            bank_account_amount: bankAccountAmount,
            notes,
          },
        })
        .then(toReceiptBatch),
    onSuccess: (data) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PAGINATED_KEY({ nursingHomeId }),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_AND_RECEIPT_BATCHES_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({ queryKey: RECEIPT_BATCH_KEY(data.id) }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_PHOENIX_KEY(data.id),
        }),
        queryClient.invalidateQueries({ queryKey: CASH_LIST_TRANSACTIONS_KEY }),
        queryClient.invalidateQueries({
          queryKey: CASH_LIST_TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_LISTS_PHOENIX_KEY(),
        }),
        ...(nursingHomeId
          ? [
              queryClient.invalidateQueries({
                queryKey: CASH_LIST_CONFIGURATION_PHOENIX_KEY(nursingHomeId),
              }),
            ]
          : []),
      ]),
  });
};

export const usePostReceiptBatchEntryPhoenix = (): UseMutationResult<
  ReceiptBatchResult,
  unknown,
  { batchId: string; data: UpdateReceiptBatchEntry[] }
> => {
  const queryClient = useQueryClient();
  const api = useReceiptBatchPhoenixApi();
  const result = useMutation<
    ReceiptBatchResult,
    unknown,
    { batchId: string; data: UpdateReceiptBatchEntry[] }
  >({
    mutationKey: CREATE_RECEIPT_BATCH_ENTRY,
    mutationFn: (data) =>
      api.postReceiptBatchEntry({
        params: { batchId: data.batchId },
        body: { data: data.data },
      }),
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_KEY(variables.batchId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_PHOENIX_KEY(variables.batchId),
        }),

        queryClient.invalidateQueries({
          queryKey: [RECEIPT_BATCH_LIST_KEY(undefined)],
        }),
        queryClient.invalidateQueries({
          queryKey: PLIANT_TRANSACTIONS_KEY(),
        }),
      ]);
    },
  });
  return result;
};

export const usePatchReceiptBatchEntryPhoenix = (
  nursingHomeId: string,
  options?: UseMutationOptions<
    ReceiptBatchResult,
    unknown,
    UpdateReceiptBatchEntry & {
      id: string;
      batchId: string;
    }
  >
): UseMutationResult<
  ReceiptBatchResult,
  unknown,
  UpdateReceiptBatchEntry & {
    id: string;
    batchId: string;
  }
> => {
  const queryClient = useQueryClient();
  const api = useReceiptBatchPhoenixApi();
  const result = useMutation<
    ReceiptBatchResult,
    unknown,
    UpdateReceiptBatchEntry & {
      id: string;
      batchId: string;
    }
  >({
    mutationKey: UPDATE_RECEIPT_BATCH_ENTRY,
    mutationFn: (data) =>
      api.patchReceiptBatchEntry({
        params: {
          batchId: data.batchId,
          entryId: data.id!,
        },
        body: data,
      }),
    onSuccess: (data, variables, optionsIn, context) => {
      const promise = options?.onSuccess?.(data, variables, optionsIn, context);
      return Promise.all([
        ...(promise ? [promise] : []),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PAGINATED_KEY({
            nursingHomeId: nursingHomeId,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_AND_RECEIPT_BATCHES_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_KEY(variables.batchId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_PHOENIX_KEY(variables.batchId),
        }),

        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_PAYMENT_STATUS(variables.batchId),
        }),
        queryClient.invalidateQueries({
          queryKey: PLIANT_TRANSACTIONS_KEY(),
        }),
        // Invalidate cash transaction groups when receipt batch entry is updated
        // The cash transaction group's linked amounts depend on receipt batch entry amounts
        ...(data.data.cash_list_transaction_group_id
          ? [
              queryClient.invalidateQueries({
                queryKey: CASH_TRANSACTION_GROUP_PHOENIX_KEY(
                  data.data.cash_list_transaction_group_id
                ),
              }),
              queryClient.invalidateQueries({
                queryKey: CASH_TRANSACTION_GROUP_KEY(
                  data.data.cash_list_transaction_group_id
                ),
              }),
            ]
          : []),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: CASH_TRANSACTION_GROUP_LIST_KEY,
        }),
      ]);
    },
    ...options,
  });
  return result;
};

export const useDeleteReceiptBatchPhoenix = (
  nursingHomeId?: string
): UseMutationResult<
  { status: 'OK' },
  unknown,
  {
    batchId: string;
  }
> => {
  const queryClient = useQueryClient();
  const api = useReceiptBatchPhoenixApi();
  const result = useMutation<
    { status: 'OK' },
    unknown,
    {
      batchId: string;
    }
  >({
    mutationKey: DELETE_RECEIPT_BATCH,
    mutationFn: (data) =>
      api.deleteReceiptBatch({
        params: { batchId: data.batchId },
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PAGINATED_KEY({ nursingHomeId }),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_AND_RECEIPT_BATCHES_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: PLIANT_TRANSACTIONS_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PHOENIX_KEY(),
        }),
      ]);
    },
  });
  return result;
};

export const useDeleteReceiptBatchEntryPhoenix = ({
  nursingHomeId,
}: {
  nursingHomeId: string;
}): UseMutationResult<
  ReceiptBatchResult,
  unknown,
  {
    batchId: string;
    entryId: string;
  }
> => {
  const api = useReceiptBatchPhoenixApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    ReceiptBatchResult,
    unknown,
    {
      batchId: string;
      entryId: string;
    }
  >({
    mutationKey: DELETE_RECEIPT_BATCH_ENTRY_KEY,
    mutationFn: (data) =>
      api.deleteReceiptBatchEntry({
        params: { batchId: data.batchId, entryId: data.entryId },
      }),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_KEY(variables.batchId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_PHOENIX_KEY(variables.batchId),
        }),

        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_PAYMENT_STATUS(variables.batchId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PAGINATED_KEY({
            nursingHomeId: nursingHomeId,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_AND_RECEIPT_BATCHES_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: PLIANT_TRANSACTIONS_KEY(),
        }),
      ]);
    },
  });
  return result;
};

export const useDeleteReceiptBatchEntriesPhoenix = ({
  nursingHomeId,
}: {
  nursingHomeId: string;
}): UseMutationResult<
  ReceiptBatchResult,
  unknown,
  {
    batchId: string;
    entryIds: string[];
  }
> => {
  const api = useReceiptBatchPhoenixApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    ReceiptBatchResult,
    unknown,
    {
      batchId: string;
      entryIds: string[];
    }
  >({
    mutationKey: ['DELETE_RECEIPT_BATCH_ENTRIES'],
    mutationFn: (data) =>
      api.deleteReceiptBatchEntries({
        params: { batchId: data.batchId },
        body: { entry_ids: data.entryIds },
      }),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_KEY(variables.batchId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_PHOENIX_KEY(variables.batchId),
        }),

        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_PAYMENT_STATUS(variables.batchId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PAGINATED_KEY({
            nursingHomeId: nursingHomeId,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_AND_RECEIPT_BATCHES_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: PLIANT_TRANSACTIONS_KEY(),
        }),
      ]);
    },
  });
  return result;
};

/**
 * Hook for submitting a receipt batch asynchronously using an Oban job.
 * Returns the updated receipt batch with submission_oban_job_id set.
 */
export const useSubmitReceiptBatchAsyncPhoenix = ({
  nursingHomeId,
}: {
  nursingHomeId?: string;
}): UseMutationResult<
  ReceiptBatch,
  unknown,
  {
    batchId: string;
  }
> => {
  const api = useReceiptBatchPhoenixApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    ReceiptBatch,
    unknown,
    {
      batchId: string;
    }
  >({
    mutationKey: ['SUBMIT_RECEIPT_BATCH_ASYNC'],
    mutationFn: (data) =>
      api
        .submitReceiptBatchAsync({
          params: { batchId: data.batchId },
        })
        .then(toReceiptBatch),
    onSuccess: async (_, variables) => {
      // Invalidate the batch to show the pending job status
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_KEY(variables.batchId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_PHOENIX_KEY(variables.batchId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PHOENIX_KEY(),
        }),
      ]);
    },
  });
  return result;
};

const RECEIPT_BATCH_JOB_STATUS_KEY = (batchId: string | undefined) => [
  'RECEIPT_BATCH_JOB_STATUS',
  batchId,
];

/**
 * Hook for polling the status of a receipt batch submission job.
 * Use refetchInterval to poll at regular intervals.
 */
export const useGetReceiptBatchJobStatus = (
  batchId: string | undefined,
  options?: Omit<
    UseQueryOptions<ReceiptBatchJobResult, unknown, ReceiptBatchJob>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<ReceiptBatchJob, unknown> => {
  const isEnabled = batchId !== undefined && (options?.enabled ?? true);

  const select = useCallback(
    (data: ReceiptBatchJobResult): ReceiptBatchJob => ({
      ...data.data,
      submission_date: new Date(data.data.submission_date),
    }),
    []
  );

  return useApiQuery(
    useReceiptBatchPhoenixApi,
    RECEIPT_BATCH_JOB_STATUS_KEY(batchId),
    (api) => {
      if (batchId === undefined) {
        throw new Error('batchId is undefined');
      }
      return api.getReceiptBatchJobStatus({
        params: { batchId },
      });
    },
    {
      select,
      enabled: isEnabled,
      ...options,
    }
  );
};

/**
 * Hook that handles the full async submission flow:
 * 1. Submits the batch async
 * 2. Polls for completion
 * 3. Invalidates queries on success
 */
export const useAsyncBatchSubmissionWithPolling = ({
  nursingHomeId,
  pollingIntervalMs = 1000,
  onComplete,
  onError,
}: {
  nursingHomeId?: string;
  pollingIntervalMs?: number;
  onComplete?: () => void;
  onError?: (error: unknown) => void;
}): {
  submitAsync: (batchId: string) => Promise<void>;
  isSubmitting: boolean;
  jobStatus: ReceiptBatchJob | undefined;
  isPolling: boolean;
} => {
  const queryClient = useQueryClient();
  const [currentBatchId, setCurrentBatchId] = useState<string | undefined>(
    undefined
  );

  const submitMutation = useSubmitReceiptBatchAsyncPhoenix({ nursingHomeId });

  const jobStatusQuery = useGetReceiptBatchJobStatus(currentBatchId, {
    enabled: currentBatchId !== undefined,
    refetchInterval: (query) => {
      // query.state.data is the selected type (ReceiptBatchJob)
      const data = query.state.data as ReceiptBatchJob | undefined;
      // Stop polling when job is complete or failed
      if (data?.status === 'successful' || data?.status === 'failed') {
        return false;
      }
      return pollingIntervalMs;
    },
  });

  // Handle completion
  useEffect(() => {
    if (jobStatusQuery.data?.status === 'successful') {
      // Invalidate all relevant queries
      Promise.all([
        ...(nursingHomeId
          ? [
              queryClient.invalidateQueries({
                queryKey: RESIDENTS_PHOENIX_KEY(nursingHomeId),
              }),
            ]
          : []),
        queryClient.invalidateQueries({
          queryKey: RESIDENTS_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({ queryKey: ALL_SHOW_RESIDENT_KEY() }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PAGINATED_KEY({
            nursingHomeId: nursingHomeId,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_KEY_NURSING_HOME(nursingHomeId, {}),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPTS_AND_RECEIPT_BATCHES_KEY(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: getFinanceKey(nursingHomeId),
        }),
        queryClient.invalidateQueries({
          queryKey: TRANSACTIONS_PHOENIX_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: PLIANT_TRANSACTIONS_KEY(),
        }),
        queryClient.invalidateQueries({
          queryKey: RECEIPT_BATCH_LIST_PHOENIX_KEY(),
        }),
      ]).then(() => {
        setCurrentBatchId(undefined);
        onComplete?.();
      });
    } else if (jobStatusQuery.data?.status === 'failed') {
      setCurrentBatchId(undefined);
      onError?.(new Error('Receipt batch submission failed'));
    }
  }, [
    jobStatusQuery.data?.status,
    nursingHomeId,
    queryClient,
    onComplete,
    onError,
  ]);

  const submitAsync = useCallback(
    async (batchId: string) => {
      await submitMutation.mutateAsync({ batchId });
      // Start polling using batchId (the endpoint now uses batchId, not jobId)
      setCurrentBatchId(batchId);
    },
    [submitMutation]
  );

  return {
    submitAsync,
    isSubmitting: submitMutation.isPending,
    jobStatus: jobStatusQuery.data,
    isPolling: currentBatchId !== undefined && jobStatusQuery.isFetching,
  };
};
